import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Users, CheckCircle, Calendar } from 'lucide-react';
import axios from 'axios';
import { formatPhoneNumber, isValidPhoneNumber, isValidEmail } from '../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MemberSignup = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Host selection states
  const [availableHosts, setAvailableHosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [hostSearchLoading, setHostSearchLoading] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    date_of_birth: '',
    gender: '',
    phone_number: '',
    city: '',
    state: '',
    country: '',
    terms_accepted: true,

    // Step 2: Professional Information
    industry: '',
    expertise_areas: [],
    company: '',
    employment_status: '',
    job_title: '',
    website_url: '',
    professional_bio: '',

    // Step 3: Marketing Activities
    google_business_url: '',
    facebook_page_url: '',
    instagram_profile_url: '',
    linkedin_profile_url: '',

    // Step 4: Host Selection (NEW STEP)
    selected_host_id: null,
    selected_host_code: '',
    is_general_member: true,
    host_selection_method: 'general', // 'general', 'code', 'search', 'browse'

    // Step 5: Additional Information  
    interests_hobbies: [],
    volunteer_experience_years: '',
    education_background: '',
    languages_spoken: [],

    // Step 6: Subscription
    subscribe_magazine: true
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let processedValue = value;

    // Format phone number as user types
    if (name === 'phone_number') {
      processedValue = formatPhoneNumber(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Real-time password confirmation validation
    if (name === 'confirm_password' || (name === 'password' && formData.confirm_password)) {
      const password = name === 'password' ? processedValue : formData.password;
      const confirmPassword = name === 'confirm_password' ? processedValue : formData.confirm_password;

      if (confirmPassword && password !== confirmPassword) {
        setErrors(prev => ({ ...prev, confirm_password: 'Passwords do not match' }));
      } else {
        setErrors(prev => ({ ...prev, confirm_password: '' }));
      }
    }

    // Real-time email validation
    if (name === 'email' && processedValue) {
      if (!isValidEmail(processedValue)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }
  };

  const handleMultiSelect = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [name]: newValues };
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!isValidEmail(formData.email)) newErrors.email = 'Please enter a valid email address';
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
      if (!formData.phone_number.trim()) newErrors.phone_number = 'Phone number is required';
      else if (!isValidPhoneNumber(formData.phone_number)) newErrors.phone_number = 'Please enter a valid 10-digit phone number';
      if (!formData.city.trim()) newErrors.city = 'City is required';
      if (!formData.state.trim()) newErrors.state = 'State is required';
      if (!formData.country) newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Host selection functions
  const searchHosts = async (search = '', location = '') => {
    setHostSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (location) params.append('city', location);

      const response = await axios.get(`${API}/hosts/available?${params.toString()}`);
      setAvailableHosts(response.data.hosts || []);
    } catch (error) {
      console.error('Host search error:', error);
      setErrors({ host_search: 'Failed to search hosts. Please try again.' });
    } finally {
      setHostSearchLoading(false);
    }
  };

  const searchHostByCode = async (code) => {
    if (!code || code.length < 3) return;

    setHostSearchLoading(true);
    try {
      const response = await axios.get(`${API}/hosts/by-code/${code}`);
      if (response.data.host) {
        setSelectedHost(response.data.host);
        setFormData(prev => ({
          ...prev,
          selected_host_id: response.data.host.id,
          selected_host_code: code.toUpperCase(),
          is_general_member: false,
          host_selection_method: 'code'
        }));
        setErrors({});
      }
    } catch (error) {
      console.error('Host code search error:', error);
      if (error.response?.status === 404) {
        setErrors({ host_code: 'Host code not found. Please check and try again.' });
      } else {
        setErrors({ host_code: 'Failed to find host. Please try again.' });
      }
      setSelectedHost(null);
    } finally {
      setHostSearchLoading(false);
    }
  };

  const selectHost = (host) => {
    setSelectedHost(host);
    setFormData(prev => ({
      ...prev,
      selected_host_id: host.id,
      selected_host_code: host.host_code,
      is_general_member: false,
      host_selection_method: 'search'
    }));
    setErrors({});
  };

  const selectGeneralMembership = () => {
    setSelectedHost(null);
    setFormData(prev => ({
      ...prev,
      selected_host_id: null,
      selected_host_code: '',
      is_general_member: true,
      host_selection_method: 'general'
    }));
    setErrors({});
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const signupData = {
        step1: {
          full_name: formData.full_name,
          email: formData.email,
          password: formData.password,
          role: 'member',
          phone_number: formData.phone_number,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          date_of_birth: formData.date_of_birth,
          gender: formData.gender,
          terms_accepted: formData.terms_accepted
        },
        step2: {
          industry: formData.industry,
          expertise_areas: formData.expertise_areas,
          company: formData.company,
          employment_status: formData.employment_status,
          job_title: formData.job_title,
          website_url: formData.website_url,
          professional_bio: formData.professional_bio
        },
        step3: {
          google_business_url: formData.google_business_url,
          facebook_page_url: formData.facebook_page_url,
          instagram_profile_url: formData.instagram_profile_url,
          linkedin_profile_url: formData.linkedin_profile_url
        },
        step4: {
          selected_host_id: formData.selected_host_id,
          selected_host_code: formData.selected_host_code,
          is_general_member: formData.is_general_member
        },
        step5: {
          interests_hobbies: formData.interests_hobbies,
          volunteer_experience_years: parseInt(formData.volunteer_experience_years) || 0,
          education_background: formData.education_background,
          languages_spoken: formData.languages_spoken
        },
        subscribe_magazine: formData.subscribe_magazine
      };

      const response = await axios.post(`${API}/auth/signup/member`, signupData);

      // Store user info for payment process
      localStorage.setItem('signup_email', formData.email);
      localStorage.setItem('signup_role', 'member');

      // navigate('/email-verification', { 
      //   state: { 
      //     email: formData.email, 
      //     message: response.data.message,
      //     requiresPayment: response.data.requires_payment 
      //   } 
      // });
      navigate(`/verify-email`, {
        state: {
          requiresPayment: response.data.requires_payment,
          message: response.data.message,
        }
      });

    } catch (error) {
      console.error('Signup error:', error);
      if (error.response?.data?.detail?.errors) {
        setErrors(error.response.data.detail.errors);
        setCurrentStep(1); // Go back to first step if validation errors
      } else {
        setErrors({ general: error.response?.data?.detail || 'Signup failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia',
    'Japan', 'South Korea', 'Singapore', 'India', 'Brazil', 'Mexico', 'Other'
  ];

  const industries = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'Marketing', 'Real Estate',
    'Consulting', 'Manufacturing', 'Retail', 'Hospitality', 'Media', 'Other'
  ];

  const expertiseAreas = [
    'Digital Marketing', 'Sales', 'Project Management', 'Data Analytics', 'Design',
    'Software Development', 'Finance', 'HR', 'Operations', 'Strategy', 'Legal'
  ];

  const employmentStatuses = ['Self-employed', 'Employed', 'Student', 'Partner'];

  const interests = [
    'Networking', 'Public Speaking', 'Mentoring', 'Innovation', 'Travel',
    'Sports', 'Reading', 'Technology', 'Art', 'Music', 'Cooking'
  ];

  const educationLevels = [
    'High School', 'Bachelor\'s Degree', 'Master\'s Degree', 'PhD', 'Professional Certification'
  ];

  const languages = [
    'English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Portuguese', 'Other'
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            {/* Member Icon */}
            <div className="flex justify-center mb-6">
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
              }}>
                <Users className="w-8 h-8 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-white mb-4 text-center">Personal Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Full Name *</label>
                <Input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                />
                {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create password (min 8 characters)"
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Confirm Password *</label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    placeholder="Confirm password"
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirm_password && <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Date of Birth</label>
                <div className="relative">
                  <Input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-600"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Phone Number *</label>
              <Input
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                maxLength="14"
              />
              {errors.phone_number && <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">City *</label>
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                />
                {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">State *</label>
                <Input
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
                />
                {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Country *</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-600"
                >
                  <option value="">Select country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
              />
              <label className="text-sm text-gray-300">
                I agree to the Terms & Conditions and Privacy Policy
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4">Professional Information</h3>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Industry</label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="">Select industry</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Areas of Expertise/Interests</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {expertiseAreas.map(area => (
                  <div key={area} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.expertise_areas.includes(area)}
                      onCheckedChange={() => handleMultiSelect('expertise_areas', area)}
                    />
                    <label className="text-sm text-gray-300">{area}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Company/Organization</label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Employment Status</label>
                <select
                  name="employment_status"
                  value={formData.employment_status}
                  onChange={handleInputChange}
                  className="w-full h-10 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                >
                  <option value="">Select status</option>
                  {employmentStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Job Title</label>
                <Input
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleInputChange}
                  placeholder="Enter job title"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Website URL</label>
                <Input
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Professional Bio/Introduction</label>
              <Textarea
                name="professional_bio"
                value={formData.professional_bio}
                onChange={handleInputChange}
                placeholder="Tell us about your professional background..."
                className="bg-gray-800 border-gray-700 text-white"
                rows={4}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4">Marketing Activities</h3>
            <p className="text-gray-400 text-sm mb-6">Share your social media profiles to connect with other members (optional)</p>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Google Business Review URL</label>
              <Input
                name="google_business_url"
                value={formData.google_business_url}
                onChange={handleInputChange}
                placeholder="https://google.com/business/..."
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Facebook Page URL</label>
              <Input
                name="facebook_page_url"
                value={formData.facebook_page_url}
                onChange={handleInputChange}
                placeholder="https://facebook.com/..."
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Instagram Profile URL</label>
              <Input
                name="instagram_profile_url"
                value={formData.instagram_profile_url}
                onChange={handleInputChange}
                placeholder="https://instagram.com/..."
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">LinkedIn Profile URL</label>
              <Input
                name="linkedin_profile_url"
                value={formData.linkedin_profile_url}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/..."
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500"
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Choose Your Host</h3>
            <p className="text-gray-400 text-sm mb-6">
              Select how you'd like to connect with the Success Club community. You can choose a specific host/location or join our general community.
            </p>

            {/* Host Selection Options */}
            <div className="space-y-4">
              {/* Success Club General Option */}
              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${formData.host_selection_method === 'general'
                  ? 'border-amber-500 bg-amber-500/10'
                  : 'border-gray-600 hover:border-gray-500'
                  }`}
                onClick={selectGeneralMembership}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-white">Success Club General</h4>
                    <p className="text-gray-400 text-sm">Join our general community with access to all hosts and events</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${formData.host_selection_method === 'general'
                    ? 'border-amber-500 bg-amber-500'
                    : 'border-gray-400'
                    }`} />
                </div>
              </div>

              {/* Host Code Option */}
              <div className={`border-2 rounded-lg p-4 ${formData.host_selection_method === 'code'
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-gray-600'
                }`}>
                <h4 className="text-lg font-semibold text-white mb-2">Join with Host Code</h4>
                <p className="text-gray-400 text-sm mb-3">Have a specific host code? Enter it below:</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter host code"
                    value={formData.selected_host_code}
                    onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setFormData(prev => ({ ...prev, selected_host_code: code }));
                      if (code.length >= 3) {
                        searchHostByCode(code);
                      }
                    }}
                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-400 uppercase"
                    maxLength={10}
                  />
                  <Button
                    onClick={() => searchHostByCode(formData.selected_host_code)}
                    disabled={!formData.selected_host_code || hostSearchLoading}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {hostSearchLoading ? 'Searching...' : 'Find'}
                  </Button>
                </div>
                {errors.host_code && <p className="text-red-500 text-sm mt-1">{errors.host_code}</p>}

                {selectedHost && formData.host_selection_method === 'code' && (
                  <div className="mt-3 p-3 bg-gray-800 rounded border border-gray-700">
                    <h5 className="text-white font-medium">{selectedHost.business_name || selectedHost.coworking_space_name || selectedHost.full_name}</h5>
                    <p className="text-gray-400 text-sm">{selectedHost.city}, {selectedHost.state}</p>
                    <p className="text-gray-400 text-sm">{selectedHost.member_count} members</p>
                  </div>
                )}
              </div>

              {/* Browse Hosts Option */}
              <div className={`border-2 rounded-lg p-4 ${formData.host_selection_method === 'search'
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-gray-600'
                }`}>
                <h4 className="text-lg font-semibold text-white mb-2">Browse Available Hosts</h4>
                <p className="text-gray-400 text-sm mb-3">Search for hosts by name or location:</p>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search by name or business"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-400"
                    />
                    <Input
                      placeholder="City or State"
                      value={searchLocation}
                      onChange={(e) => setSearchLocation(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-400"
                    />
                    <Button
                      onClick={() => searchHosts(searchTerm, searchLocation)}
                      disabled={hostSearchLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {hostSearchLoading ? 'Searching...' : 'Search'}
                    </Button>
                  </div>

                  {errors.host_search && <p className="text-red-500 text-sm">{errors.host_search}</p>}

                  {/* Host Results */}
                  {availableHosts.length > 0 && (
                    <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-700 rounded p-2">
                      {availableHosts.map(host => (
                        <div
                          key={host.id}
                          className={`p-3 rounded border cursor-pointer transition-all ${selectedHost?.id === host.id
                            ? 'border-amber-500 bg-amber-500/10'
                            : 'border-gray-700 hover:border-gray-600'
                            }`}
                          onClick={() => selectHost(host)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="text-white font-medium">{host.business_name || host.coworking_space_name || host.full_name}</h5>
                              <p className="text-gray-400 text-sm">{host.city}, {host.state}</p>
                              <p className="text-gray-400 text-sm">{host.member_count} members</p>
                            </div>
                            <div className={`w-4 h-4 rounded-full border-2 ${selectedHost?.id === host.id
                              ? 'border-amber-500 bg-amber-500'
                              : 'border-gray-400'
                              }`} />
                          </div>
                          {host.mission_vision && (
                            <p className="text-gray-400 text-xs mt-2 truncate">{host.mission_vision}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Selection Summary */}
            <div className="mt-6 p-4 bg-gray-800 rounded border border-gray-700">
              <h5 className="text-white font-medium mb-2">Your Selection:</h5>
              {formData.is_general_member ? (
                <p className="text-gray-300">Success Club General - Access to all hosts and events</p>
              ) : selectedHost ? (
                <div>
                  <p className="text-white">{selectedHost.business_name || selectedHost.coworking_space_name || selectedHost.full_name}</p>
                  <p className="text-gray-400 text-sm">{selectedHost.city}, {selectedHost.state}</p>
                  {selectedHost.host_code && (
                    <p className="text-gray-400 text-sm">Host Code: {selectedHost.host_code}</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-400">No host selected</p>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-4">Additional Information</h3>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Interests/Hobbies</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {interests.map(interest => (
                  <div key={interest} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.interests_hobbies.includes(interest)}
                      onCheckedChange={() => handleMultiSelect('interests_hobbies', interest)}
                    />
                    <label className="text-sm text-gray-300">{interest}</label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Volunteer Experience (Years)</label>
              <Input
                type="number"
                name="volunteer_experience_years"
                value={formData.volunteer_experience_years}
                onChange={handleInputChange}
                placeholder="Enter years of experience"
                className="bg-gray-800 border-gray-700 text-white"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Education Background</label>
              <select
                name="education_background"
                value={formData.education_background}
                onChange={handleInputChange}
                className="w-full h-10 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="">Select education level</option>
                {educationLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Languages Spoken</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {languages.map(language => (
                  <div key={language} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.languages_spoken.includes(language)}
                      onCheckedChange={() => handleMultiSelect('languages_spoken', language)}
                    />
                    <label className="text-sm text-gray-300">{language}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Complete Your Membership</h3>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-white">Success Club Membership</h4>
                <div className="text-2xl font-bold text-amber-500">$15/month</div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Access to exclusive business network</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Monthly Success Club magazine included</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Event attendance and networking opportunities</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Challenge participation and point system</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Community chat and messaging</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Checkbox
                  checked={formData.subscribe_magazine}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, subscribe_magazine: checked }))}
                />
                <label className="text-sm text-gray-300">
                  Include Success Club magazine (already included in membership)
                </label>
              </div>
            </div>

            <div className="bg-amber-600/10 border border-amber-600/20 rounded-lg p-4">
              <p className="text-amber-200 text-sm">
                <strong>Next steps:</strong> After completing signup, you'll receive an email verification link.
                Once verified, you'll be redirected to complete your payment and gain full access to Success Club.
              </p>
            </div>

            {errors.general && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-400 text-sm">{errors.general}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div
                className="flex items-center justify-center bg-white shadow-lg overflow-hidden"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png"
                  alt="Success Club Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    scale: '1.1'
                  }}
                />
              </div>
            </div>
            <CardTitle className="text-2xl text-white flex items-center justify-center gap-2">
              Member Registration
            </CardTitle>

            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div
                  key={step}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    ...(step === currentStep
                      ? {
                        background: 'linear-gradient(135deg, #D4AF37 0%, #C29A4B 100%)',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(212, 175, 55, 0.4)'
                      }
                      : step < currentStep
                        ? {
                          background: '#fff',
                          border: '2px solid #D4AF37',
                          color: '#D4AF37'
                        }
                        : {
                          background: '#374151',
                          color: '#9ca3af'
                        })
                  }}
                >
                  {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
              ))}
            </div>
            <p className="text-gray-400 mt-2">Step {currentStep} of 6</p>
          </CardHeader>

          <CardContent>
            {renderStep()}

            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-800">
              <div className="flex gap-2">
                {currentStep > 1 && (
                  <Button
                    onClick={prevStep}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}

                <Button
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                >
                  Cancel
                </Button>
              </div>

              <div>
                {currentStep < 6 ? (
                  <div className="flex gap-2">
                    {(currentStep === 2 || currentStep === 3 || currentStep === 4 || currentStep === 5) && (
                      <Button
                        onClick={() => setCurrentStep(prev => prev + 1)}
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-800"
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Skip
                      </Button>
                    )}
                    <Button
                      onClick={nextStep}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {loading ? 'Creating Account...' : 'Complete Registration'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberSignup;